import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { reportService } from "../services/reportService";
import { reportDisplayId } from "./ReportCommonStore";
import { Report } from "../../../api/graphql";
import { reportTypeDisplayId } from "../../reportType/store/ReportTypeCommonStore";
import { reportTypeService } from "../../reportType/services/reportTypeService";
import { InitializeParams } from "../../../store/types";

export class ReportViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = reportService;
  reportInstance: Report | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  reportTypeService = reportTypeService;
  reportTypeSearchValue: string = "";
  reportTypeIdValue: string = "";
  reportTypeSelectedValue: { displayId: string; id: string } | null = null;
  templateValue: string = "";
  constantsValue: string = "";
  descriptionValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadReportById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.reportOne({ id });
    this.reportInstance = result.reportOne;
    if (this.reportInstance) {
      this.loadValues(this.reportInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.reportInstance) {
      return reportDisplayId(this.reportInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.ReportSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "reportType") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.reportTypeOne({
          id: params.returnData.resultId,
        });
        const reportType = result.reportTypeOne;
        this.reportTypeSelectedValue = reportType;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get reportTypeValue() {
    if (this.reportTypeSelectedValue) {
      return {
        id: this.reportTypeSelectedValue.id,
        displayId: reportTypeDisplayId(
          this.reportTypeSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.reportInstance = null;
    this.nameValue = "";
    this.reportTypeSearchValue = "";
    this.reportTypeIdValue = "";
    this.templateValue = "";
    this.constantsValue = "";
    this.descriptionValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelReport() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("report_update");
  }

  editReport() {
    const id = this.reportInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/report/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printReport(handlePrint: () => void): void {
    handlePrint();
  }

  *closeReport(): any {
    yield this.close();
  }

  loadValues(instance: Report) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.reportTypeSelectedValue = instance.reportType;
    this.templateValue = instance.template;
    this.constantsValue = instance.constants || "";
    this.descriptionValue = instance.description || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadReportById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
